import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faUserGroup } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { NavigatingButton } from '@sorare/core/src/atoms/buttons/NavigatingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { HeadlineXS, LabelS } from '@sorare/core/src/atoms/typography';
import {
  ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_PRIZE_POOL,
  ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_PRIZE_POOL_CONTENDER,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { CumulativeRankRewards } from 'components/leaderboard/CumulativeRankRewards';
import type { Props as RankRewardsProps } from 'components/leaderboard/RankRewards';
import { RankRewards } from 'components/leaderboard/RankRewards';
import { isFixtureOpened } from 'lib/so5';

import type {
  CumulativePrizePool_so5Leaderboard,
  CumulativePrizePool_so5LeaderboardContender,
} from './__generated__/index.graphql';

enum TabValue {
  GW = 'GW',
  CUMULATIVE = 'CUMULATIVE',
}

const RankRewardsContainer = styled.div`
  border-radius: var(--double-unit);
  background-color: var(--c-nd-100);
  padding: var(--double-unit);
`;

export type Props = {
  so5Leaderboard: CumulativePrizePool_so5Leaderboard;
  so5LeaderboardContender?: CumulativePrizePool_so5LeaderboardContender;
  withCollapsibleRanking?: boolean;
  rankingLink?: (
    fixtureSlug: string,
    userGroupSlug: string,
    userSlug: string
  ) => string;
} & Pick<RankRewardsProps, 'onClickRanking'>;

export const CumulativePrizePool = ({
  so5Leaderboard,
  so5LeaderboardContender,
  withCollapsibleRanking,
  rankingLink,
  onClickRanking,
}: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  const [tabValue, setTabValue] = useState(TabValue.GW);
  const { generateSportContextPath } = useSportContext();
  const userGroup = so5Leaderboard.universalSo5UserGroups[0];
  const isUpcoming =
    userGroup.startSo5Fixture && isFixtureOpened(userGroup.startSo5Fixture);
  const isGameWeekUpcoming = isFixtureOpened(so5Leaderboard.so5Fixture);
  const RewardsContainer = isUpcoming ? RankRewardsContainer : 'div';
  const { seasonGameWeek, gameWeek } = so5Leaderboard.so5Fixture;

  return (
    <Vertical gap={2}>
      <SBHorizontal>
        {!isGameWeekUpcoming ? (
          <TabBar
            value={tabValue}
            variant="flat"
            onValueChange={newVal => setTabValue(newVal as TabValue)}
          >
            <Tab value={TabValue.GW}>
              <FormattedMessage
                {...fantasy.gw}
                values={{ week: seasonGameWeek || gameWeek }}
              />
            </Tab>
            <Tab value={TabValue.CUMULATIVE}>
              <FormattedMessage {...fantasy.shortCumulativeLeaderboard} />
            </Tab>
          </TabBar>
        ) : (
          <HeadlineXS as="h3">
            <FormattedMessage {...fantasy.cumulativeLeaderboard} />
          </HeadlineXS>
        )}
        {!!isTabletAndAbove && (
          <Horizontal>
            <FontAwesomeIcon icon={faUserGroup} size="xs" />
            <LabelS as="p" bold>
              <FormattedNumber
                value={
                  isGameWeekUpcoming || tabValue === TabValue.CUMULATIVE
                    ? userGroup.membershipsCount
                    : so5Leaderboard.so5LineupsCount
                }
              />
            </LabelS>
          </Horizontal>
        )}
      </SBHorizontal>
      <RewardsContainer>
        {!!(isGameWeekUpcoming || tabValue === TabValue.CUMULATIVE) && (
          <CumulativeRankRewards
            userGroupSlug={so5Leaderboard.universalSo5UserGroups[0].slug}
            activeLineupId={so5LeaderboardContender?.so5Lineup?.id}
            withCollapsibleRanking={withCollapsibleRanking}
            rankingLink={rankingLink}
          />
        )}
        {tabValue === TabValue.GW && !isGameWeekUpcoming && (
          <RankRewards
            onClickRanking={onClickRanking}
            so5Leaderboard={so5Leaderboard}
            so5Ranking={so5LeaderboardContender?.so5Lineup?.so5Rankings[0]}
          />
        )}
      </RewardsContainer>
      {!isUpcoming && (
        <Portal id="contender-drawer-content-sticky-footer">
          <NavigatingButton
            color="quaternary"
            size="medium"
            to={generateSportContextPath(
              so5LeaderboardContender
                ? ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_PRIZE_POOL_CONTENDER
                : ANY_SPORT_PLAY_EVENTTYPE_LEADERBOARD_LEADERBOARD_PRIZE_POOL,
              {
                params: {
                  eventType: so5Leaderboard.so5Fixture.type.toLowerCase(),
                  leaderboard: so5Leaderboard.slug,
                  contender: so5LeaderboardContender?.slug || '',
                },
              }
            )}
          >
            <FormattedMessage {...fantasy.viewLeaderboard} />
          </NavigatingButton>
        </Portal>
      )}
    </Vertical>
  );
};

CumulativePrizePool.fragments = {
  so5Leaderboard: gql`
    fragment CumulativePrizePool_so5Leaderboard on So5Leaderboard {
      slug
      so5LineupsCount
      so5Fixture {
        slug
        gameWeek
        seasonGameWeek
        type
        ...isFixtureOpened_so5Fixture
      }
      universalSo5UserGroups {
        slug
        membershipsCount
        startSo5Fixture {
          slug
          ...isFixtureOpened_so5Fixture
        }
      }
      ...RankRewards_so5Leaderboard
    }
    ${isFixtureOpened.fragments.so5Fixture}
    ${RankRewards.fragments.so5Leaderboard}
  ` as TypedDocumentNode<CumulativePrizePool_so5Leaderboard>,
  so5LeaderboardContender: gql`
    fragment CumulativePrizePool_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Lineup {
        id
        so5Rankings {
          id
          ...RankRewards_so5Ranking
        }
      }
    }
    ${RankRewards.fragments.so5Ranking}
  ` as TypedDocumentNode<CumulativePrizePool_so5LeaderboardContender>,
};
